/**
 * 메인 라우터
 */

import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAdminAuthStore } from '../../admin/auth/store/authStore';
import { useFrontAuthStore } from '../../front/auth/store/authStore';
import { ALL_ROUTES } from './routes';

// Auth Components
import AdminLoginForm from '../../admin/auth/components/AdminLoginForm';
import FrontLoginForm from '../../front/auth/components/FrontLoginForm';
import RegisterTermsAgreement from '../../front/auth/components/RegisterTermsAgreement';
import RegisterForm from '../../front/auth/components/RegisterForm';
import RegisterComplete from '../../front/auth/components/RegisterComplete';
import FindIdForm from '../../front/auth/components/FindIdForm';
import FindIdResult from '../../front/auth/components/FindIdResult';
import ResetPasswordForm from '../../front/auth/components/ResetPasswordForm';
import ResetPasswordNew from '../../front/auth/components/ResetPasswordNew';
import ResetPasswordComplete from '../../front/auth/components/ResetPasswordComplete';

// Protected Route Components
import AdminProtectedRoute from '../../admin/auth/components/AdminProtectedRoute';
import FrontProtectedRoute from '../../front/auth/components/FrontProtectedRoute';

// Pages
import AdminDashboard from '../../admin/pages/AdminDashboard';
import AdminUsers from '../../admin/pages/AdminUsers';
import UserDashboard from '../../front/pages/UserDashboard/UserDashboard';
import UserProfile from '../../front/pages/UserProfile/UserProfile';
import BookList from '../../front/pages/BookList/BookList';
// import MockBookViewer from '../../front/pages/MockBookViewer/MockBookViewer'; // MockBookViewer 미사용
import BookViewer from '../../front/pages/BookViewer/BookViewer';
import { AttachmentProvider } from '../../front/contexts/AttachmentContext';
import InquiryPage from '../../front/pages/Inquiry/Inquiry';
import InquiryCreatePage from '../../front/pages/Inquiry/InquiryCreate';
import InquiryEditPage from '../../front/pages/Inquiry/InquiryEdit';
import LandingPage from '../../front/pages/LandingPage/LandingPage';
import NotFoundPage from '../../core/error/NotFoundPage';
import UnauthorizedPage from '../../core/error/UnauthorizedPage';

const AppRouter: React.FC = () => {
  const { checkAuth: checkAdminAuth, isAuthenticated: isAdminAuthenticated } = useAdminAuthStore();
  const { checkAuth: checkFrontAuth, isAuthenticated: isFrontAuthenticated } = useFrontAuthStore();

  useEffect(() => {
    // 앱 시작 시 인증 상태 확인
    checkAdminAuth();
    checkFrontAuth();
  }, [checkAdminAuth, checkFrontAuth]);

  return (
    <Routes>
      {/* 공개 라우트 - 인증 불필요 */}
      <Route path={ALL_ROUTES.LOGIN} element={<FrontLoginForm />} />
      <Route path={ALL_ROUTES.REGISTER_TERMS} element={<RegisterTermsAgreement />} />
      <Route path={ALL_ROUTES.REGISTER_FORM} element={<RegisterForm />} />
      <Route path={ALL_ROUTES.REGISTER_COMPLETE} element={<RegisterComplete />} />
      <Route path={ALL_ROUTES.REGISTER} element={<Navigate to={ALL_ROUTES.REGISTER_TERMS} replace />} />
      <Route path={ALL_ROUTES.FIND_ID} element={<FindIdForm />} />
      <Route path={ALL_ROUTES.FIND_ID_RESULT} element={<FindIdResult />} />
      <Route path={ALL_ROUTES.RESET_PASSWORD} element={<ResetPasswordForm />} />
      <Route path={ALL_ROUTES.RESET_PASSWORD_NEW} element={<ResetPasswordNew />} />
      <Route path={ALL_ROUTES.RESET_PASSWORD_COMPLETE} element={<ResetPasswordComplete />} />

      {/* 관리자 전용 라우트 */}
      <Route 
        path="/admin/login" 
        element={
          <AdminProtectedRoute redirectIfAuthenticated>
            <AdminLoginForm />
          </AdminProtectedRoute>
        } 
      />
      
      <Route
        path={ALL_ROUTES.ADMIN_DASHBOARD}
        element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        }
      />
      
      <Route
        path={ALL_ROUTES.ADMIN_USERS}
        element={
          <AdminProtectedRoute>
            <AdminUsers />
          </AdminProtectedRoute>
        }
      />

      {/* 사용자 전용 라우트 */}
      <Route
        path={ALL_ROUTES.DASHBOARD}
        element={
          <FrontProtectedRoute>
            <UserDashboard />
          </FrontProtectedRoute>
        }
      />
      
      <Route
        path={ALL_ROUTES.PROFILE}
        element={
          <FrontProtectedRoute>
            <UserProfile />
          </FrontProtectedRoute>
        }
      />
      
      <Route
        path={ALL_ROUTES.BOOK_LIST}
        element={<BookList />}
      />
      
      {/* MockBookViewer 미사용
      <Route
        path={ALL_ROUTES.BOOK_VIEWER}
        element={
          <FrontProtectedRoute>
            <MockBookViewer />
          </FrontProtectedRoute>
        }
      />
      */}
      
      <Route
        path={ALL_ROUTES.API_BOOK_VIEWER}
        element={
          <FrontProtectedRoute>
            <AttachmentProvider>
              <BookViewer />
            </AttachmentProvider>
          </FrontProtectedRoute>
        }
      />
      
      <Route
        path={ALL_ROUTES.INQUIRY_CREATE}
        element={
          <FrontProtectedRoute>
            <InquiryCreatePage />
          </FrontProtectedRoute>
        }
      />
      
      <Route
        path={ALL_ROUTES.INQUIRY_EDIT}
        element={
          <FrontProtectedRoute>
            <InquiryEditPage />
          </FrontProtectedRoute>
        }
      />
      
      <Route
        path={ALL_ROUTES.INQUIRY}
        element={
          <FrontProtectedRoute>
            <InquiryPage />
          </FrontProtectedRoute>
        }
      />

      {/* 공통 페이지 */}
      <Route path={ALL_ROUTES.UNAUTHORIZED} element={<UnauthorizedPage />} />
      <Route path={ALL_ROUTES.NOT_FOUND} element={<NotFoundPage />} />

      {/* 기본 경로 - 랜딩 페이지 */}
      <Route path={ALL_ROUTES.HOME} element={<LandingPage />} />

      {/* 404 처리 */}
      <Route path="*" element={<Navigate to={ALL_ROUTES.NOT_FOUND} replace />} />
    </Routes>
  );
};

export default AppRouter;